import { Link } from "@tanstack/react-router";
import type { CollabTriggerType, SequenceTriggerType } from "@unbox/api";
import { useEffect, useRef, useState } from "react";
import { useSequenceDefaults } from "@/api/sequences";
import { Button } from "@/components/base/buttons/button";
import { useCollabTriggerTypes } from "@/pages/sequences/collab-trigger-types";
import { SequenceEditorDialog } from "@/pages/sequences/sequence-editor-dialog";
import { TRIGGER_LABELS } from "@/pages/sequences/sequence-steps-preview";
import { useMerchant } from "@/providers/MerchantProvider";
import { error as toastError } from "@/providers/ToastProvider";
import { cx } from "@/utils/cx";

export type AutomationEntry = {
  /** Whether the trigger is active for this collab (true = enabled, false = disabled for this collab) */
  enabled: boolean;
  /** Sequence ID for the custom override, or null if using the merchant's global default */
  sequenceId: string | null;
};

export type AutomationsFormState = Record<CollabTriggerType, AutomationEntry>;

export const INITIAL_AUTOMATIONS: AutomationsFormState = {
  collaboration_invite: { enabled: false, sequenceId: null },
  sample_delivered: { enabled: false, sequenceId: null },
  creator_dormant: { enabled: false, sequenceId: null },
  first_collab_video: { enabled: false, sequenceId: null },
};

type Mode = "disabled" | "default" | "custom";

const MODE_OPTIONS: { value: Mode; label: string }[] = [
  { value: "disabled", label: "Disabled" },
  { value: "default", label: "Default" },
  { value: "custom", label: "Custom" },
];

function entryToMode(entry: AutomationEntry): Mode {
  if (!entry.enabled) return "disabled";
  if (entry.sequenceId) return "custom";
  return "default";
}

function ModePill({ value, onChange }: { value: Mode; onChange: (next: Mode) => void }) {
  return (
    <div className="inline-flex items-center rounded-full bg-secondary p-0.5">
      {MODE_OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            className={cx(
              "px-3 py-1 text-xs font-medium rounded-full transition-colors",
              active ? "bg-primary text-primary shadow-sm" : "text-tertiary hover:text-secondary",
            )}
            onClick={() => !active && onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function AutomationRow({
  triggerType,
  entry,
  globalDefault,
  defaultsLoaded,
  collaborationName,
  onChange,
}: {
  triggerType: SequenceTriggerType;
  entry: AutomationEntry;
  globalDefault: { sequenceId: string; sequenceName: string } | null;
  defaultsLoaded: boolean;
  collaborationName: string;
  onChange: (next: AutomationEntry) => void;
}) {
  const [editorOpen, setEditorOpen] = useState(false);
  // When the editor was opened by switching INTO Custom from another mode, remember the
  // entry that was active beforehand — if the user dismisses the dialog without saving we
  // restore it. Null when the editor is opened to edit an already-saved custom (cancel is
  // a no-op in that case). Uses a ref (not state) because onSaved and onOpenChange fire in
  // the same render cycle, and state would still read the pre-onSaved value in the second
  // callback.
  const revertToOnCancelRef = useRef<AutomationEntry | null>(null);

  // If the row is on "Default" but the merchant has since removed the global default,
  // there's nothing to run. Coerce to Disabled so the UI and the saved form state agree.
  // Wait for the defaults query to resolve — otherwise we'd coerce every "Default" override
  // on mount before the query responds.
  const isStrandedDefault = defaultsLoaded && entry.enabled && !entry.sequenceId && !globalDefault;
  useEffect(() => {
    if (isStrandedDefault) {
      onChange({ enabled: false, sequenceId: null });
    }
  }, [isStrandedDefault, onChange]);

  const mode = entryToMode(entry);

  const statusText =
    mode === "disabled"
      ? "Will not run."
      : mode === "default"
        ? "Uses global default."
        : "Runs a custom automation for this collaboration.";

  const handleModeChange = (next: Mode) => {
    if (next === "disabled") {
      onChange({ enabled: false, sequenceId: null });
      return;
    }
    if (next === "default") {
      if (!globalDefault) {
        // Keep the previous selection — just tell the user why the transition was refused.
        toastError(`No global default set for "${TRIGGER_LABELS[triggerType] ?? triggerType}" yet.`);
        return;
      }
      onChange({ enabled: true, sequenceId: null });
      return;
    }
    // custom — capture the pre-transition entry so we can restore it if they cancel.
    revertToOnCancelRef.current = entry;
    setEditorOpen(true);
  };

  const handleEditorOpenChange = (open: boolean) => {
    setEditorOpen(open);
    if (!open && revertToOnCancelRef.current) {
      // Dialog dismissed before Save during a Custom transition — restore the prior entry.
      onChange(revertToOnCancelRef.current);
      revertToOnCancelRef.current = null;
    }
  };

  const seedFromSequenceId = entry.sequenceId ? null : (globalDefault?.sequenceId ?? null);

  return (
    <div className="flex flex-col rounded-lg border border-secondary bg-primary p-4 gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-primary">{TRIGGER_LABELS[triggerType] ?? triggerType}</p>
        <ModePill value={mode} onChange={handleModeChange} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <p className={cx("text-sm", mode === "disabled" ? "italic text-quaternary" : "text-tertiary")}>{statusText}</p>
        {mode === "custom" && (
          <Button
            color="secondary"
            size="sm"
            className="px-2! py-1! text-xs!"
            onClick={() => {
              revertToOnCancelRef.current = null;
              setEditorOpen(true);
            }}
          >
            Edit automation
          </Button>
        )}
      </div>

      <SequenceEditorDialog
        open={editorOpen}
        onOpenChange={handleEditorOpenChange}
        triggerType={triggerType}
        scope={collaborationName}
        sequenceId={entry.sequenceId}
        seedFromSequenceId={seedFromSequenceId}
        onSaved={(savedId, created) => {
          revertToOnCancelRef.current = null;
          if (created) {
            onChange({ enabled: true, sequenceId: savedId });
          }
        }}
      />
    </div>
  );
}

export function AutomationsStep({
  value,
  onChange,
  collaborationName,
}: {
  value: AutomationsFormState;
  onChange: (next: AutomationsFormState) => void;
  collaborationName: string;
}) {
  const { data: defaults = [], isSuccess: defaultsLoaded } = useSequenceDefaults();
  const merchantId = useMerchant();
  const triggerTypes = useCollabTriggerTypes();
  const defaultMap = new Map(defaults.filter((d) => d.enabled).map((d) => [d.triggerType, d]));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-primary">Automation triggers</p>
          <p className="text-sm text-tertiary">Configure what runs when each event fires for this collaboration.</p>
        </div>
        <Link
          to="/merchant/$merchantId/automate-outreach"
          params={{ merchantId }}
          target="_blank"
          className="text-sm text-brand-secondary hover:text-brand-tertiary underline shrink-0"
        >
          Edit global defaults
        </Link>
      </div>

      <div className="flex flex-col gap-3">
        {triggerTypes.map((triggerType) => (
          <AutomationRow
            key={triggerType}
            triggerType={triggerType}
            entry={value[triggerType]}
            globalDefault={defaultMap.get(triggerType) ?? null}
            defaultsLoaded={defaultsLoaded}
            collaborationName={collaborationName}
            onChange={(next) => onChange({ ...value, [triggerType]: next })}
          />
        ))}
      </div>
    </div>
  );
}
